import * as PIXI from 'pixi.js';
import GameConfig from './GameConfig';
import GameModel from './GameModel';
import {Location, Loc} from 'src/common/map/Location';
import {WallPiece, RotationDirection} from 'src/common/map/WallPiece';
import GameUtils from './GameUtils';
import {MapState} from 'src/common/map/MapState';
import {Game} from './Game';

import Point = PIXI.Point;
import Container = PIXI.Container;
import Sprite = PIXI.Sprite;

export class WallPieceCursor {
    private container: Container;
    private _tile: Point = new Point();
    private _wallPiece: WallPiece;
    private _canBuild: boolean;
    private sprites: Sprite[] = [];
    private pivot?: Sprite;

    public constructor(private game: Game) {
        // Sets instance properties
    }

    public get tile(): Point {
        return this._tile;
    }

    public get canBuild(): boolean {
        return this._canBuild;
    }

    public get wallPiece(): WallPiece {
        return this._wallPiece;
    }

    public updatePiece(newWallPiece: WallPiece, map: MapState, index: number): void {
        if (
            newWallPiece &&
            (!this.wallPiece ||
                !this.isSameWallPiece(this.wallPiece, newWallPiece) ||
                this.container.children.length === 0)
        ) {
            this._wallPiece = newWallPiece;
            this.makeWallPiece();
            this.updateCanBuild(map, index);
        }
    }

    private isSameWallPiece(a: WallPiece, b: WallPiece): boolean {
        return (
            a.tiles.length === b.tiles.length &&
            a.tiles.every((loc, i) => b.tiles[i].x === loc.x && b.tiles[i].y === loc.y)
        );
    }

    public updatePosition(tile: Point, map: MapState, index: number): void {
        this._tile = tile;
        if (!this.wallPiece) return;

        const pos = this.spritePosition(tile);
        this.container.position.set(pos.x, pos.y);
        this.updateCanBuild(map, index);
    }

    public rotate(direction: RotationDirection, map: MapState, index: number): void {
        if (!this.wallPiece) return;

        this.wallPiece.rotate(direction);
        this.makeWallPiece();
        this.updateCanBuild(map, index);
    }

    public remove(): void {
        if (this.container) {
            const children = [...this.container.children];
            children.forEach(child => this.container.removeChild(child));
        }
        this.sprites = [];
        this.pivot = undefined;
    }

    private updateCanBuild(map: MapState, index: number): void {
        const canBuildAt = this.game.mode.canBuildAtLocation.bind(this.game.mode);
        this._canBuild = map.canBuildWallPiece(
            index,
            this.tile.x,
            this.tile.y,
            this.wallPiece.rotation,
            this.wallPiece.tiles,
            canBuildAt
        );
        this.sprites.forEach((child, i) => {
            child.tint = this.canBuildAtLocation(map, index, this.wallPiece.tiles[i])
                ? 0xffffff
                : 0xff0000;
        });
        if (this.pivot) {
            this.pivot.tint = this.canBuild ? 0xafafaf : 0x8f0000;
        }
    }

    private canBuildAtLocation(map: MapState, index: number, loc: Loc): boolean {
        if (this.game.mode.showBuildability) {
            const canBuildAt = this.game.mode.canBuildAtLocation.bind(this.game.mode);
            return map.canBuildWallPiece(
                index,
                this.tile.x,
                this.tile.y,
                this.wallPiece.rotation,
                [loc],
                canBuildAt
            );
        } else {
            return this.canBuild;
        }
    }

    private spritePosition(tile: Point): Point {
        const size = GameConfig.tileSize;
        return new Point(tile.x * size, tile.y * size);
    }

    private makeWallPiece(): void {
        this.remove();
        if (!this.container) {
            this.container = new Container();
            this.container.position.set(
                this.tile.x * GameConfig.tileSize,
                this.tile.y * GameConfig.tileSize
            );
        }

        this.sprites = [];
        const base = new Location(0, 0);
        this.wallPiece.rotatedLocations(base).forEach(loc => {
            const texture = GameUtils.spriteTexture('castle-tiles/white');
            const sprite = new Sprite(texture);
            sprite.position.set(loc.x * GameConfig.tileSize, loc.y * GameConfig.tileSize);
            sprite.scale.set(2, 2);
            this.container.addChild(sprite);
            this.sprites.push(sprite);
        });
        this.container.addChild(this.makePivot());
        GameModel.cursorContainer.addChild(this.container);
    }

    private makePivot(): Sprite {
        const texture = GameUtils.spriteTexture('cannonball');
        const sprite = new PIXI.Sprite(texture);
        sprite.anchor.set(0.5, 0.5);
        sprite.position.set(GameConfig.tileSize / 2, GameConfig.tileSize / 2);
        sprite.scale.set(0.3, 0.3);
        sprite.tint = 0x202020;
        this.pivot = sprite;
        return sprite;
    }
}



// WEBPACK FOOTER //
// ./src/game/WallPieceCursor.ts